import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

import CTA from 'Src/components/buttons/CTA';
import Copy from 'Src/components/Copy';
import { Flex } from 'Src/components/layout/Flex';
import { Check, CheckItemDef } from 'Src/feature/check/models';
import { useCheckItemDiscounts } from 'Src/feature/check-item-discount/useCheckItemDiscounts';
import { useLogger } from 'Src/feature/context-logger/useLogger';
import { DiscountPromoCodeInput } from 'Src/feature/discount/DiscountPromoCodeInput';
import { OpenDiscountModal } from 'Src/feature/discount/OpenDiscountModal';
import { Divider } from 'Src/feature/divider/Divider';
import { FormField } from 'Src/feature/form/FormField';
import { Modal } from 'Src/feature/modal/Modal';
import { ModalAction } from 'Src/feature/modal/ModalAction';
import { ModalActions } from 'Src/feature/modal/ModalActions';
import { ModalContent } from 'Src/feature/modal/ModalContent';
import { ModalHeader } from 'Src/feature/modal/ModalHeader';
import { ModalTitle } from 'Src/feature/modal/ModalTitle';

import styles from './CheckItemDiscountModal.module.scss';

const sectionId = 'CheckItemDiscountModal';

interface CheckItemDiscountModalProps {
  open: boolean;
  onDone: () => void;
  check: Check | undefined;
  checkItemIds: CheckItemDef['id'][];
}

const CheckItemDiscountModalContent: FC<CheckItemDiscountModalProps> = ({ onDone, check, checkItemIds }) => {
  const { t } = useTranslation();
  const log = useLogger().getSubLogger({ name: sectionId });

  const { store } = useCheckItemDiscounts({
    contextId: sectionId,
    checkId: check?.id,
    checkItemIds: checkItemIds,
  });

  return (
    <>
      <ModalHeader>
        <ModalTitle copy={t('discount.label')} />
      </ModalHeader>
      <ModalContent>
        {store.has.someWithPromoCodes && (
          <DiscountPromoCodeInput
            inputProps={{
              placeholder: t('discount.labels.promoCode'),
            }}
            getDiscountFromPromoCode={store.getDiscountFromPromoCode}
            onApply={(promoCode) => {
              const matchingDiscount = store.getDiscountFromPromoCode(promoCode);
              if (matchingDiscount) {
                store.active.promoCode = store.getPromoCodeFromDiscount(promoCode, matchingDiscount);
                store.onDiscountAdd(matchingDiscount);
              }
            }}
          />
        )}
        {(store.autoDiscounts.length > 0) && (
          <Flex gap="xxl" className={styles.autoDiscount}>
            <Flex gap="sm">
              <Divider variant="stubby" color="light" />
              <Copy variant="body" size="lg" weight="md" value={t('discount.labels.autoApplied')} />
            </Flex>
            <div className={styles.grid}>
              {store.autoDiscounts.map((d) => {
                let formattedDiscountValue = '';
                if (d.type === 'Amount') formattedDiscountValue = t('labels.util.currencyNegative', { value: d.value });
                else if (d.type === 'Percent') formattedDiscountValue = t('labels.util.percentNegative', { value: d.value });
                return (
                  <React.Fragment key={d.id}>
                    <Copy variant="body" weight="regular" value={d.name} />
                    <Copy variant="body" weight="regular" color={d.isAutoDisabled ? 'textDisabled' : undefined} value={formattedDiscountValue} />
                    <FormField
                      type="switch"
                      checked={!d.isAutoDisabled}
                      onChange={(_, value) => {
                        if (!store.updateAutoDiscount.isPending) {
                          store.onUpdateAutoDiscount({ standardDiscountId: d.standardDiscountId as number, isAutoDisabled: !value, itemId: store.checkItems[0].id });
                        }
                      }}
                    />
                    <Divider className={styles.divider} color="dark" />
                  </React.Fragment>
                );
              })}
            </div>
          </Flex>
        )}
        <Flex className={styles.manualDiscount}>
          <div className={styles.grid}>
            {store.discounts?.map((discount) => {
              let formattedDiscountValue = '';
              const onCheckValue = (discount.onCheckItems ?? []).find((item) => checkItemIds.includes(item.itemId ?? ''))?.value;
              if (discount.type === 'FixedAmount') formattedDiscountValue = t('labels.util.currencyNegative', { value: discount.fixedValue });
              else if (discount.type === 'FixedPercent') formattedDiscountValue = t('labels.util.percentNegative', { value: discount.fixedValue });
              else if (discount.type === 'OpenAmount') formattedDiscountValue = t('labels.util.currencyNegative', { value: onCheckValue ?? 0 });
              else if (discount.type === 'OpenPercent') formattedDiscountValue = t('labels.util.percentNegative', { value: onCheckValue ?? 0 });
              return (
                <React.Fragment key={discount.id}>
                  <CTA
                    variant="text-inline"
                    className={styles.cta}
                    minWidth="self"
                    label={discount.name}
                    onClick={() => {
                      if (discount.is.applied) {
                        store.onDiscountEdit(discount);
                      }
                    }}
                  />
                  <CTA
                    variant="text-inline"
                    className={styles.cta}
                    minWidth="self"
                    disabled={!discount.is.applied}
                    label={formattedDiscountValue}
                    onClick={() => {
                      if (discount.is.applied) {
                        store.onDiscountEdit(discount);
                      }
                    }}
                  />
                  <FormField
                    type="switch"
                    checked={discount.is.applied}
                    onChange={() => {
                      if (discount.is.applied) store.onDiscountRemove(discount);
                      else store.onDiscountAdd(discount);
                    }}
                  />
                  <Divider className={styles.divider} color="dark" />
                </React.Fragment>
              );
            })}
          </div>
        </Flex>
      </ModalContent>
      <ModalActions>
        <ModalAction color="secondary" label={t('labels.done')} onClick={onDone} />
      </ModalActions>
      <OpenDiscountModal
        open={store.modal.openDiscount.isOpen}
        discount={store.active.discount}
        unpaidAmount={store.combined.menuItem.price}
        checkAmount={store.combined.menuItem.price}
        isEditing={!!store.active.discount?.is.applied}
        initialNote={store.active.note}
        initialAmount={store.active.openValue}
        initialReasonId={store.active.reason?.id}
        onApply={({ reasonId, amount, note }) => {
          if (typeof reasonId !== 'number') return log.debug('OpenDiscountModal.onApply !reasonId');
          store.modal.openDiscount.onApply(reasonId, parseFloat(amount?.toString() ?? '0'), note);
        }}
        onCancel={store.modal.openDiscount.close}
      />
    </>
  );
};

// eslint-disable-next-line react/no-multi-comp
const CheckItemDiscountModal: FC<CheckItemDiscountModalProps> = ({ open, check, checkItemIds, onDone }) => (
  <Modal
    data-testid={sectionId}
    open={open}
    onBackDropClick={onDone}
    variant="grow"
  >
    <CheckItemDiscountModalContent
      open={open}
      check={check}
      checkItemIds={checkItemIds}
      onDone={onDone}
    />
  </Modal>
);

export { CheckItemDiscountModal, sectionId };
